import { api } from '../api/client';

/**
 * The room's PROCESSING TIME ESTIMATE and per-stage tally (owner ask
 * 2026-10-23), shared by the admin and the seller apps and the end-of-upload
 * summary: GET /rooms/:id/processing/estimate. The server does the arithmetic
 * (src/modules/documents/processing-estimate.ts); this is the contract and the
 * plain-words formatting, so every screen says the same thing.
 */
export type EstimateStageKey = 'filing' | 'ocr' | 'redaction';

export interface EstimateStage {
  key: EstimateStageKey;
  /** Documents past this stage. */
  done: number;
  /** Documents that pass through it. */
  total: number;
  /** Documents still to go (queued, running, or not yet arrived). */
  remaining: number;
  /** Estimated seconds until this stage is finished; null when nothing remains. */
  secondsLeft: number | null;
  /**
   * Whether the pages behind the time were read from the files (`known`) or
   * assumed from their size (`assumed`). Older servers said `recent` / `typical` /
   * `default` (a per-document pace); nothing reads it.
   */
  basis: 'known' | 'assumed' | 'recent' | 'typical' | 'default';
}

export interface ProcessingEstimate {
  total: number;
  documentsLeft: number;
  /** Estimated seconds for everything; null when nothing remains. */
  secondsLeft: number | null;
  /** In order; the redaction stage is absent when the room's redaction is off. */
  stages: EstimateStage[];
  allDone: boolean;
  /** When allDone: how long the latest batch took, start to finish. */
  tookSeconds: number | null;
  generatedAt: string;
}

export const STAGE_LABEL: Record<EstimateStageKey, string> = {
  filing: 'Filing',
  ocr: 'Reading scans',
  redaction: 'Redaction',
};

/** How often the tally refreshes while work remains. */
export const ESTIMATE_POLL_MS = 10_000;

export const estimateQueryKey = (roomId: string) => ['rooms', roomId, 'processing', 'estimate'];

export function fetchProcessingEstimate(roomId: string): Promise<ProcessingEstimate> {
  return api.get<ProcessingEstimate>(`/rooms/${roomId}/processing/estimate`);
}

/** The poll interval: every ~10 s while work remains, none once it is all done (or on an error). */
export function estimateRefetchInterval(
  data: ProcessingEstimate | undefined,
  errored: boolean,
): number | false {
  if (errored || !data) return false;
  return data.documentsLeft > 0 ? ESTIMATE_POLL_MS : false;
}

/** Longer than this reads "more than 3 hours": past it the number is a guess. */
export const TIME_CAP_SECONDS = 3 * 3600;

/**
 * A duration in plain, honest words — never exact, never zero or negative:
 * "under a minute", "about 1 minute", "about 5 minutes", "about 35 minutes",
 * "about 1 hour", "about 1 hour 20 minutes", "more than 3 hours". Under 10
 * minutes rounds to the minute, then to 5 minutes. Null for a value that is not
 * a number. Callers use it only while work remains (or for a finished duration).
 */
export function formatTimeLeft(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return null;
  if (seconds > TIME_CAP_SECONDS) return 'more than 3 hours';
  if (seconds < 60) return 'under a minute';
  const minutes = seconds / 60;
  if (minutes < 10) {
    const n = Math.max(1, Math.round(minutes));
    return `about ${n} ${n === 1 ? 'minute' : 'minutes'}`;
  }
  const total = Math.round(minutes / 5) * 5;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `about ${mins} minutes`;
  const h = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return mins === 0 ? `about ${h}` : `about ${h} ${mins} minutes`;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "About 5 minutes left" / "Under a minute left" / "More than 3 hours left". */
export function timeLeftHeadline(seconds: number | null | undefined): string | null {
  const t = formatTimeLeft(seconds);
  return t === null ? null : `${capitalize(t)} left`;
}

/** Whether `v` has the estimate's shape (an older server or a stub may answer something else). */
export function isProcessingEstimate(v: unknown): v is ProcessingEstimate {
  const e = v as Partial<ProcessingEstimate> | null | undefined;
  return (
    typeof e === 'object' &&
    e !== null &&
    typeof e.total === 'number' &&
    typeof e.documentsLeft === 'number' &&
    Array.isArray(e.stages)
  );
}

/** The end-of-upload line: "Processing these will take about 5 minutes." (null when nothing is left). */
export function uploadProcessingLine(estimate: ProcessingEstimate | undefined): string | null {
  if (!isProcessingEstimate(estimate) || estimate.documentsLeft <= 0) return null;
  const t = formatTimeLeft(estimate.secondsLeft ?? 1);
  return t === null ? null : `Processing these will take ${t}.`;
}
